import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { inr } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useBatch } from "@/context/BatchContext";
import ItemMedia from "@/components/itemCard/ItemMedia";
import ItemAction from "@/components/itemCard/ItemAction";

export default function ItemCard({ item, index, defaultSize, onAdd, activeBatch }) {
  const price = defaultSize ? defaultSize.price : item.price;
  const pricePending = Boolean(item.price_pending);
  const { items, updateQty } = useCart();
  const available = item.available !== false;
  const { dailyCounts, display } = useBatch();
  const daySoldOut = Boolean(dailyCounts?.sold_out_categories?.includes(item.category));
  const batchSoldOut = (activeBatch && activeBatch.status === "sold_out") || daySoldOut;
  const effectiveAvailable = available && !batchSoldOut;
  // qty stepper only for items that add directly (not customize-modal-driven)
  const canStep = !item.customizable && effectiveAvailable;
  const line = canStep ? items.find((l) => l.key === item.id) : null;
  const isPizza = item.category === "roman";

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.5, delay: (index % 4) * 0.06, type: "spring", stiffness: 90 }}
      whileHover={effectiveAvailable ? { y: -6 } : {}}
      className={`group bg-white border-2 border-biga-ink/10 rounded-2xl overflow-hidden flex flex-col ${
        effectiveAvailable ? "hover:border-biga-orange hover:shadow-[0_18px_40px_-12px_rgba(255,80,3,0.35)]" : "opacity-70"
      } transition-colors`}
      data-testid={`menu-item-${item.id}`}
    >
      <ItemMedia item={item} interactive={effectiveAvailable} />

      <div className="p-4 sm:p-5 flex flex-col flex-1">
        {isPizza ? (
          <Link to={`/pizza/${item.id}`} className="font-display font-bold text-xl sm:text-2xl leading-none text-biga-ink hover:text-biga-orange transition-colors">{item.name}</Link>
        ) : (
          <h3 className="font-display font-bold text-xl sm:text-2xl leading-none text-biga-ink">{item.name}</h3>
        )}
        {isPizza && <p className="text-xs font-medium text-biga-ink/60 mt-1.5 tracking-wide" data-testid={`dough-line-${item.id}`}>72-hour fermented BIGA dough</p>}
        <p className="text-sm text-biga-ink/60 mt-2 font-medium line-clamp-2 flex-1">{item.description}</p>
        <div className="flex flex-wrap gap-1 mt-3">
          {item.ingredients.slice(0, 3).map((ing) => (
            <span key={ing} className="text-[10px] font-semibold uppercase tracking-wide text-biga-green/80 bg-biga-green/8 px-2 py-0.5 rounded-full">
              {ing}
            </span>
          ))}
        </div>
        <div className="flex items-center justify-between mt-4">
          <div>
            <span data-testid={`price-${item.id}`} className="font-display font-black text-2xl text-biga-orange">{pricePending ? "Price pending" : inr(price)}</span>
            {item.original_price && (
              <span className="ml-2 text-sm font-bold text-biga-ink/35 line-through">{inr(item.original_price)}</span>
            )}
          </div>
          <ItemAction item={item} defaultSize={defaultSize} onAdd={onAdd} line={line} updateQty={updateQty} effectiveAvailable={effectiveAvailable} batchSoldOut={batchSoldOut} soldOutLabel={display.sold_out_label} pricePending={pricePending} />
        </div>
      </div>
    </motion.div>
  );
}
